import React, { useCallback, useEffect, useState } from 'react';
import { Btn, C, Card } from '../../../components/UI';
import { platformApi, MarketplaceAppAdminDto } from '../../../services/api';
import MarketplaceAppEditForm from './MarketplaceAppEditForm';

const statusColors: Record<string, [string, string]> = {
    Draft: ['#F5F5F5', '#666'],
    Published: [C.greenBg, C.green],
    Archived: ['#FFF0F0', '#9B1C1C'],
};

export default function MarketplaceCatalogAdmin() {
    const [apps, setApps] = useState<MarketplaceAppAdminDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState<MarketplaceAppAdminDto | 'new' | null>(null);
    const [working, setWorking] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try { setApps(await platformApi.getMarketplaceApps()); }
        catch (e: any) { setError(e.message || 'Failed to load apps.'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const publish = async (id: string) => {
        setWorking(id);
        try { await platformApi.publishMarketplaceApp(id); await load(); }
        catch (e: any) { setError(e.message || 'Failed to publish.'); }
        finally { setWorking(null); }
    };

    const archive = async (id: string) => {
        if (!window.confirm('Archive this app? Tenants who already installed it keep it, but it disappears from the catalog.')) return;
        setWorking(id);
        try { await platformApi.archiveMarketplaceApp(id); await load(); }
        catch (e: any) { setError(e.message || 'Failed to archive.'); }
        finally { setWorking(null); }
    };

    if (editing) {
        return (
            <MarketplaceAppEditForm
                app={editing === 'new' ? null : editing}
                onSaved={() => { setEditing(null); load(); }}
                onCancel={() => setEditing(null)}
            />
        );
    }

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>App Marketplace catalog</h1>
                <Btn onClick={() => setEditing('new')}>+ New app</Btn>
            </div>

            {loading && <div style={{ textAlign: 'center', padding: 40, color: C.textMuted }}>Loading apps...</div>}
            {error && <div style={{ color: '#D84040', padding: '12px 16px', background: '#FFF0F0', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

            {!loading && !error && (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    {apps.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: C.textMuted }}>No apps yet.</div>}
                    {apps.map(app => {
                        const [bg, color] = statusColors[app.status] ?? statusColors.Draft;
                        return (
                            <div key={app.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderBottom: `1px solid ${C.borderLight}` }}>
                                <div style={{
                                    width: 36, height: 36, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
                                    background: C.bg, border: `1px solid ${C.border}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    {app.logoUrl
                                        ? <img src={app.logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        : <span style={{ fontSize: 14, fontWeight: 700, color: C.textMuted }}>{app.name.charAt(0)}</span>}
                                </div>
                                <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => setEditing(app)}>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{app.name}</div>
                                    <div style={{ fontSize: 12, color: C.textMuted }}>{app.category} · {app.pricingModel === 'Free' ? 'Free' : `$${app.monthlyPrice}/mo`} · {app.setupType}</div>
                                </div>
                                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: bg, color }}>{app.status}</span>
                                <Btn variant="outline" size="sm" onClick={() => setEditing(app)}>Edit</Btn>
                                {app.status !== 'Published' && (
                                    <Btn size="sm" onClick={() => publish(app.id)} disabled={working === app.id}>
                                        {working === app.id ? 'Publishing…' : 'Publish'}
                                    </Btn>
                                )}
                                {app.status !== 'Archived' && (
                                    <Btn variant="danger" size="sm" onClick={() => archive(app.id)} disabled={working === app.id}>Archive</Btn>
                                )}
                            </div>
                        );
                    })}
                </Card>
            )}
        </>
    );
}
